import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DIGIPIN_CLOUD_API_URL,
  claimDigipinApiKey,
  fetchMyDigipinAccess,
  requestDigipinAccess,
  type DigipinKeyMeta,
  type DigipinRequest,
} from '../../services/digipinAccessService';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgSurface: '#0F172A',
  bgCard: 'rgba(15, 23, 42, 0.85)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
  greenSignal: '#10B981',
};

const DigipinDevelopersPage = () => {
  const location = useLocation();
  const autoClaimParam = new URLSearchParams(location.search).get('claim') === 'true';
  const autoClaimProcessed = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [request, setRequest] = useState<DigipinRequest | null>(null);
  const [keys, setKeys] = useState<DigipinKeyMeta[]>([]);
  const [useCase, setUseCase] = useState('');
  const [organization, setOrganization] = useState('');
  const [volume, setVolume] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimedKey, setClaimedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMyDigipinAccess();
      setRequest(data.request);
      setKeys(data.keys || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load developer keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await requestDigipinAccess({
        useCase,
        organization: organization || undefined,
        estimatedMonthlyCalls: volume ? parseInt(volume, 10) : undefined,
      });
      setSuccess('Request submitted. An email notification has been sent to our lab admin.');
      setUseCase('');
      setOrganization('');
      setVolume('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const activeKey = keys.find((k) => k.status === 'active');
  const canClaim = keys.some((k) => k.has_one_time_key);
  const status = request?.status;

  const onClaimKey = useCallback(async () => {
    setClaiming(true);
    setError(null);
    try {
      const result = await claimDigipinApiKey();
      setClaimedKey(result.key);
      setSuccess('Key claimed — copy it into QGIS now. It will not be shown again.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not claim key');
    } finally {
      setClaiming(false);
    }
  }, [load]);

  useEffect(() => {
    if (!loading && canClaim && autoClaimParam && !autoClaimProcessed.current) {
      autoClaimProcessed.current = true;
      onClaimKey();
    }
  }, [loading, canClaim, autoClaimParam, onClaimKey]);

  return (
    <Box sx={{ ...publicPageSx, bgcolor: TOKENS.bgDark, color: TOKENS.textPrimary }}>
      <PublicSiteChrome active="developers" subtitle="Developer Portal" />

      <Container maxWidth="md" sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 8, md: 12 } }}>
        <Stack spacing={3} sx={{ mb: 6 }}>
          <Chip
            label="DEVELOPER & QGIS PLUGIN PORTAL"
            size="small"
            sx={{
              alignSelf: 'flex-start',
              bgcolor: 'rgba(217, 119, 6, 0.18)',
              color: TOKENS.accent,
              border: `1px solid ${TOKENS.accentGlow}`,
              fontWeight: 800,
              fontSize: 10,
              letterSpacing: '0.08em',
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
            DIGIPIN Cloud API Keys
          </Typography>
          <Typography sx={{ color: TOKENS.textSecondary, fontSize: '1.05rem', lineHeight: 1.6 }}>
            Request a personal API key for high-speed online batch encoding in the DIGIPIN Encoder
            Pro QGIS plugin. Keys are reviewed and approved by lab administrators.
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Status Card */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            bgcolor: TOKENS.bgCard,
            border: `1px solid ${TOKENS.borderActive}`,
            borderRadius: 3,
            boxShadow: `0 12px 40px rgba(0, 0, 0, 0.4)`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            API Access Status
          </Typography>

          {loading ? (
            <Typography sx={{ color: TOKENS.textSecondary }}>Loading key status...</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ color: TOKENS.textSecondary }}>Status:</Typography>
                <Chip
                  size="small"
                  label={(status || 'No Request').toUpperCase()}
                  sx={{
                    bgcolor:
                      status === 'approved'
                        ? 'rgba(16, 185, 129, 0.2)'
                        : status === 'pending'
                          ? 'rgba(217, 119, 6, 0.2)'
                          : 'rgba(255, 255, 255, 0.1)',
                    color:
                      status === 'approved'
                        ? TOKENS.greenSignal
                        : status === 'pending'
                          ? TOKENS.accent
                          : TOKENS.textSecondary,
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                />
              </Stack>

              {request?.admin_note && (
                <Typography sx={{ fontSize: '0.9rem', color: TOKENS.textHighlight }}>
                  Admin Note: {request.admin_note}
                </Typography>
              )}

              {activeKey && (
                <Typography sx={{ fontSize: '0.9rem', color: TOKENS.textPrimary }}>
                  Active Key Prefix:{' '}
                  <code style={{ color: TOKENS.accent }}>{activeKey.key_prefix}…</code>
                  {canClaim
                    ? ' — a full key is ready to claim below.'
                    : ' — active for QGIS requests.'}
                </Typography>
              )}

              {canClaim && (
                <Alert
                  severity="info"
                  action={
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<KeyIcon />}
                      disabled={claiming}
                      onClick={onClaimKey}
                      sx={{
                        bgcolor: TOKENS.accent,
                        color: '#FFF',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#B45309' },
                      }}
                    >
                      {claiming ? 'Claiming...' : 'Claim My Key'}
                    </Button>
                  }
                  sx={{
                    bgcolor: 'rgba(30, 58, 138, 0.4)',
                    color: '#F8FAFC',
                    border: `1px solid ${TOKENS.borderActive}`,
                  }}
                >
                  Your API key was approved! Claim it once and paste it into QGIS.
                </Alert>
              )}
            </Stack>
          )}
        </Paper>

        {/* Request Form */}
        {(!status || status === 'rejected' || status === 'cancelled') && !activeKey && (
          <Paper
            component="form"
            onSubmit={onSubmit}
            sx={{
              p: 4,
              mb: 4,
              bgcolor: TOKENS.bgCard,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Request API Access
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="Use Case Description"
                required
                fullWidth
                multiline
                minRows={2}
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                placeholder="e.g. Batch encoding land survey parcel boundaries in QGIS"
                InputLabelProps={{ style: { color: TOKENS.textSecondary } }}
                InputProps={{ style: { color: TOKENS.textPrimary } }}
              />
              <TextField
                label="Organization / Company (Optional)"
                fullWidth
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                InputLabelProps={{ style: { color: TOKENS.textSecondary } }}
                InputProps={{ style: { color: TOKENS.textPrimary } }}
              />
              <TextField
                label="Estimated Monthly Requests (Optional)"
                type="number"
                fullWidth
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                InputLabelProps={{ style: { color: TOKENS.textSecondary } }}
                InputProps={{ style: { color: TOKENS.textPrimary } }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !useCase.trim()}
                sx={{
                  bgcolor: TOKENS.accent,
                  color: '#FFF',
                  fontWeight: 700,
                  py: 1.4,
                  fontSize: '0.95rem',
                  boxShadow: `0 6px 18px ${TOKENS.accentGlow}`,
                  '&:hover': { bgcolor: '#B45309' },
                }}
              >
                {submitting ? 'Submitting Request...' : 'Submit Key Request'}
              </Button>
            </Stack>
          </Paper>
        )}

        {/* QGIS Instructions */}
        <Paper
          sx={{
            p: 4,
            bgcolor: TOKENS.bgCard,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Integration Endpoint
          </Typography>
          <Box
            sx={{
              bgcolor: 'rgba(11, 19, 43, 0.9)',
              p: 2,
              borderRadius: 2,
              border: `1px solid ${TOKENS.border}`,
              fontFamily: 'monospace',
              fontSize: 13,
              mb: 2,
            }}
          >
            <code>{DIGIPIN_CLOUD_API_URL}</code>
          </Box>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => navigator.clipboard.writeText(DIGIPIN_CLOUD_API_URL)}
            sx={{ color: TOKENS.textHighlight }}
          >
            Copy Endpoint URL
          </Button>
        </Paper>
      </Container>

      {/* Key Claim Dialog */}
      <Dialog open={!!claimedKey} onClose={() => setClaimedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: TOKENS.bgSurface, color: TOKENS.textPrimary, fontWeight: 700 }}>
          Your DIGIPIN API Key
        </DialogTitle>
        <DialogContent sx={{ bgcolor: TOKENS.bgSurface }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key into QGIS now. It will not be displayed again.
          </Alert>
          <Box
            sx={{
              bgcolor: 'rgba(11, 19, 43, 0.9)',
              p: 2,
              borderRadius: 2,
              fontFamily: 'monospace',
              color: TOKENS.accent,
              wordBreak: 'break-all',
              fontSize: 14,
            }}
          >
            {claimedKey}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: TOKENS.bgSurface, p: 2 }}>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => claimedKey && navigator.clipboard.writeText(claimedKey)}
            sx={{ color: TOKENS.textHighlight }}
          >
            Copy Key
          </Button>
          <Button
            variant="contained"
            onClick={() => setClaimedKey(null)}
            sx={{ bgcolor: TOKENS.accent, color: '#FFF' }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default DigipinDevelopersPage;
